import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChartBarIcon } from "@heroicons/react/24/outline";
import { endOfMonth, format, startOfMonth } from "date-fns";
import { fetchCaseStatsByTag } from "../../../api/statistics";
import { PeriodSelector } from "../hours/period_selector";

const STATS_PERIOD_TYPES = ["month", "quarter", "year", "custom"];

function currentMonthRange() {
  const now = new Date();
  return {
    from: format(startOfMonth(now), "yyyy-MM-dd"),
    to: format(endOfMonth(now), "yyyy-MM-dd"),
  };
}

export function StatisticsOverview() {
  const { t } = useTranslation();
  const seed = useMemo(() => currentMonthRange(), []);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodType, setPeriodType] = useState("month");
  const [from, setFrom] = useState(seed.from);
  const [to, setTo] = useState(seed.to);
  const [allTime, setAllTime] = useState(true);
  const [period, setPeriod] = useState({});
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    fetchCaseStatsByTag(period)
      .then((data) => { if (active) setStats(data); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [period]);

  const handlePeriodChange = ({ periodType: nextType, from: nextFrom, to: nextTo }) => {
    setPeriodType(nextType);
    setFrom(nextFrom);
    setTo(nextTo);
    setAllTime(false);
    setPeriod({ date_from: nextFrom, date_to: nextTo });
  };

  const handleAllTime = () => {
    setAllTime(true);
    setPeriod({});
  };

  const casesByTag = stats?.cases_by_tag ?? [];

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
        <ChartBarIcon className="h-5 w-5" />
        {t("statistics.cases_by_label")}
      </h2>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className={allTime ? "opacity-60" : undefined}>
          <PeriodSelector
            periodType={periodType}
            from={from}
            to={to}
            allowedPeriodTypes={STATS_PERIOD_TYPES}
            onChange={handlePeriodChange}
            summaryLabel={allTime ? t("statistics.all_time") : undefined}
          />
        </div>
        <button
          type="button"
          aria-pressed={allTime}
          className={`rounded-lg border px-3 py-2 text-sm ${
            allTime
              ? "border-gray-300 bg-gray-50 font-medium text-gray-900"
              : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
          }`}
          onClick={handleAllTime}
        >
          {t("statistics.all_time")}
        </button>
      </div>
      <p className="mb-3 text-xs text-gray-500">{t("statistics.date_hint")}</p>
      {loading ? <div role="status" className="h-32 animate-pulse rounded bg-gray-100" /> : error ? (
        <div role="alert" className="text-sm text-red-700">{t("statistics.load_error")} <button type="button" className="underline" onClick={() => setPeriod({ ...period })}>{t("common.retry")}</button></div>
      ) : casesByTag.length === 0 ? (
        <p className="text-sm text-gray-500">{t("statistics.no_labels_with_cases")}</p>
      ) : (
        <div className="space-y-3">
          {casesByTag.map((item) => (
            <div
              key={item.tag_id}
              className="flex items-center justify-between rounded-md border border-gray-100 bg-gray-50 px-4 py-2"
            >
              <div className="flex items-center gap-2">
                {item.tag_color && (
                  <span
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: item.tag_color }}
                  />
                )}
                <span className="font-medium text-gray-900">{item.tag_name}</span>
              </div>
              <span className="text-lg font-semibold text-blue-600">
                {item.case_count}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
